// app/src/features/bugReport/useBugReportWiring.js
import { useState } from "react";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useBugReport } from "../../hooks/useBugReport.js";
import { useBugReportTriggers } from "../../hooks/useBugReportTriggers.js";

/**
 * The bug-report wiring: `lastBugObs` (the last submitted report's `{bugId, requestId, ts}`,
 * the user panel's debug line), the `useBugReport` destructure and `useBugReportTriggers` (the
 * keyboard shortcut and the three-finger tap), registered in that order. Three inputs, every one
 * an `AppInner` binding read under its own name: `activeTrace` (the submit state's in-flight
 * stream), `lastObs` (the persisted last-executed trace) and `pushSayerMsgRef` (the late-bound
 * bridge `submitBug` posts the confirmation line through); the active forecast is read through
 * `useActiveForecast()`. `setLastBugObs` is this hook's own `useState` setter, handed to
 * `useBugReport`.
 *
 * Returns the three names `AppInner` reads under its own bindings — `bugOpen` (the chat
 * composer's and chat wiring's argument, a `frameCtx` key), `setBugOpen` (the chat wiring's
 * argument) and `openBugReport` (the chat wiring's argument, the account popover's
 * `openFeedback` and a `frameCtx` key) — RAW, exactly as the hook returns them (`setBugOpen` is
 * the hook's `useState` setter, never wrapped); `lastBugObs` for the account context; and
 * `bugReportCtx`, the modal's own context: the sixteen names `BugReportModal` destructures from
 * `ctx`, each the hook's value by identity. Nothing here reads the store or runs an effect of
 * its own.
 */
export function useBugReportWiring({ activeTrace, lastObs, pushSayerMsgRef }) {
  const active = useActiveForecast();
  const [lastBugObs, setLastBugObs] = useState(null); // {bugId,requestId,ts}
  const {
    bugOpen,
    setBugOpen,
    bugDesc,
    setBugDesc,
    bugSteps,
    setBugSteps,
    bugSubmitting,
    bugMode,
    setBugMode,
    bugScreenshots,
    bugAttaching,
    bugCapturing,
    bugHidden,
    openBugReport,
    captureScreenshot,
    attachImageFiles,
    removeScreenshot,
    submitBug,
  } = useBugReport({ active, activeTrace, lastObs, setLastBugObs, pushSayerMsgRef });
  useBugReportTriggers({ openBugReport, bugOpen });
  const bugReportCtx = {
    bugMode,
    setBugMode,
    bugDesc,
    setBugDesc,
    bugSteps,
    setBugSteps,
    bugScreenshots,
    bugAttaching,
    attachImageFiles,
    removeScreenshot,
    bugSubmitting,
    bugCapturing,
    bugHidden,
    setBugOpen,
    submitBug,
    captureScreenshot,
  };
  return { bugOpen, setBugOpen, openBugReport, lastBugObs, bugReportCtx };
}
